import { Box, Stack } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { useLangContext } from "../../../context/langContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useProtectedAxiosInstance } from "../../../hooks/axiosInstance";
import CustomTable from "../../common/tables/Table";
import { useNotificationContext } from "../../../context/notificationContext";
import errorResolver from "../../../utils/error-resolver";

import { routerPaths } from "../../../constants/Paths";
import {
  WorkspaceListColumnSortable,
  WorkspaceListRequest,
} from "../../../services/Endpoints/workspaces";
import { dateFormatter } from "../../../utils/date-formatter";
import { DocumentText, EmptyWallet, Microphone } from "iconsax-react";
import { useUrl } from "../../../hooks/urlEncoding";
import { WalletListColumnFilterable, WalletListColumnSortable } from "../../../services/Endpoints/wallet/WalletListEndpoint";
import {
  FilterOperatorEnum,
  SortDirectionEnum,
} from "../../../services/Schemas/ListQueryString";
import { BroadcastingListColumnFilterable, BroadcastingListColumnSortable } from "../../../services/Endpoints/broadcast";
import LinkWithPopover from "../common/LinkWithPopover";
import { useCanAccess } from "../../../hooks/canAccess";
import { AccessEnums } from "../../../constants/accessEnum";
import { ITableSchema } from "../../../utils/types";
import useTableSchema from "../../../hooks/useTableSchema";
import NumberFilter from "../../common/tables/filters/NumberFilter";
import DateTimeFilter from "../../common/tables/filters/DateTimeFilter";
import TextFilter from "../../common/tables/filters/TextFilter";
import { LogListColumnFilterable, LogListColumnSortable } from "../../../services/Endpoints/logs/LogListEndpoints";
import useUrlSort from "../../../hooks/useUrlSort";

interface IPayment {
  id: number;
  name: string;
  updated_at: string;
  created_at: string;
  operations: string | ReactNode;
}

export interface ISupportTabsComponent {
  walletReload: boolean;
  setWalletReload: (payload: boolean) => void;
}

export default function WorkspacesTableComponent({
  walletReload,
  setWalletReload,
}: Readonly<ISupportTabsComponent>) {
  const { queryStringUrl, parseQueryString, setParamWithSearchParams } =
    useUrl();
  const parsed = parseQueryString();
  const initialPage = parsed.page ? Number(parsed.page) : 1;
  const initialRows = parsed.count ? Number(parsed.count) : 10;
  const [value, setValue] = useState("1");
  const { lang } = useLangContext();
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRows);
  const { can } = useCanAccess();

  const [dataPayment, setDataPayment] = useState<IPayment[] | []>([]);
  const [total, setTotal] = useState(0);
  const [waiting, setWaiting] = useState(false);
  const protectedInstance = useProtectedAxiosInstance();
  const { setNotification } = useNotificationContext();
  const location = useLocation();
  const { parseUrlSorts, applyUrlSorts } = useUrlSort();
  const sorts = parseUrlSorts();

  const getDataPayment = async () => {
    try {
      setWaiting(true);
      const paymentListRequest = new WorkspaceListRequest(protectedInstance);
      const resp = await paymentListRequest.send({
        page,
        count: rowsPerPage,
        ...parseQueryString(),
      });
      setTotal(resp.data.workspaces.total);
      const transactions = resp.data.workspaces.data.map(
        ({ id, created_at, updated_at, name }) => ({
          name,
          id,
          updated_at,
          created_at,
          operations: (
            <Stack
              direction="row"
              gap="3px"
              alignItems="center"
              justifyContent="center"
            >
              <LinkWithPopover
                disabled={!can(AccessEnums.AdminWalletsList)}
                title="کیف پول"
              >
                <Stack
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",
                    width: "fit-content",
                    cursor: "pointer",
                    color: (theme) => theme.palette.complement[400],
                    "&:hover": {
                      color: (theme) => theme.palette.others.link,
                      // backgroundColor: (theme) => theme.palette.others.doneBg,
                    },
                  }}
                  component={Link}
                  to={queryStringUrl(routerPaths.WALLET, {
                    filters: {
                      [WalletListColumnFilterable.WORKSPACE_ID]: {
                        column: WalletListColumnFilterable.WORKSPACE_ID,
                        operator: FilterOperatorEnum.EQUAL,
                        value: id,
                      },
                    },
                    sorts: {
                      [WalletListColumnSortable.ID]: {
                        column: WalletListColumnSortable.ID,
                        direction: SortDirectionEnum.DESC,
                      },
                    },
                  })}
                >
                  <EmptyWallet size="20" />
                </Stack>
              </LinkWithPopover>

              <LinkWithPopover
                disabled={!can(AccessEnums.AdminVoiceBroadcastsList)}
                title="پیام صوتی"
              >
                <Stack
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",
                    width: "fit-content",
                    cursor: "pointer",
                    color: (theme) => theme.palette.complement[400],
                    "&:hover": {
                      color: (theme) => theme.palette.others.link,
                      // backgroundColor: (theme) => theme.palette.others.doneBg,
                    },
                  }}
                  component={Link}
                  to={queryStringUrl(routerPaths.VOICE_BROADCASTS, {
                    filters: {
                      [BroadcastingListColumnFilterable.WORKSPACE_ID]: {
                        column: BroadcastingListColumnFilterable.WORKSPACE_ID,
                        operator: FilterOperatorEnum.EQUAL,
                        value: id,
                      },
                    },
                    sorts: {
                      [BroadcastingListColumnSortable.ID]: {
                        column: BroadcastingListColumnSortable.ID,
                        direction: SortDirectionEnum.DESC,
                      },
                    },
                  })}
                >
                  <Microphone size="20" />
                </Stack>
              </LinkWithPopover>
              <LinkWithPopover
                title="لاگ ها"
                disabled={!can(AccessEnums.AdminLogsList)}
              >
                <Stack
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",
                    width: "fit-content",
                    cursor: "pointer",
                    color: (theme) => theme.palette.complement[400],
                    "&:hover": {
                      color: (theme) => theme.palette.others.link,
                      // backgroundColor: (theme) => theme.palette.others.doneBg,
                    },
                  }}
                  component={Link}
                  to={queryStringUrl(routerPaths.LOGS, {
                    filters: {
                      [LogListColumnFilterable.WORKSPACE_ID]: {
                        column: LogListColumnFilterable.WORKSPACE_ID,
                        operator: FilterOperatorEnum.EQUAL,
                        value: id,
                      },
                    },
                    sorts: {
                      [LogListColumnSortable.ID]: {
                        column: LogListColumnSortable.ID,
                        direction: SortDirectionEnum.DESC,
                      },
                    },
                  })}
                >
                  <DocumentText size="20" />
                </Stack>
              </LinkWithPopover>
            </Stack>
          ),
        })
      );
      setDataPayment(transactions);
      setWaiting(false);
    } catch (error) {
      errorResolver({ error, setNotification });
      setWaiting(false);
      console.error(error);
    }
  };

  useEffect(() => {
    getDataPayment();
  }, [walletReload, page, rowsPerPage, value, location.search]);

  const _columns: ITableSchema[] = [
    {
      key: "id",
      label: "شناسه",
      minWidth: 170,
      align: "center",
      FilterComponent: NumberFilter,
      sortable: true,
    },
    {
      key: "name",
      label: "شماره",
      minWidth: 100,
      align: "center",
      FilterComponent: TextFilter,
    },

    {
      key: "created_at",
      label: "تاریخ ایجاد",
      minWidth: 170,
      align: "center",
      FilterComponent: DateTimeFilter,
      format: (value: string) => value && dateFormatter({ lang, date: value }),
    },
    {
      key: "updated_at",
      label: "تاریخ آپدیت",
      minWidth: 170,
      align: "center",
      FilterComponent: DateTimeFilter,
      format: (value: string) => value && dateFormatter({ lang, date: value }),
    },
    {
      key: "operations",
      label: "عملیات",
      minWidth: 170,
      align: "center",
    },
  ];
  const { filtersSchema, columns } = useTableSchema(_columns);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setParamWithSearchParams("page", String(newPage));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRows = +event.target.value;
    setRowsPerPage(newRows);
    setParamWithSearchParams("count", String(newRows));
  };

  return (
    <Box sx={{ width: "100%", typography: "body1", backgroundColor: "white" }}>
      <CustomTable
        reloadAction={() => setWalletReload(!walletReload)}
        hasWaiting={waiting}
        hasSearchFilter
        hasBottomPagination
        hasTopPagination
        columns={columns}
        count={total}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        page={page}
        rows={dataPayment}
        rowsPerPage={rowsPerPage}
        filtersSchema={filtersSchema}
        onApplySorts={applyUrlSorts}
        sorts={sorts}
      />
    </Box>
  );
}
